import React, { useState, useEffect, useCallback } from 'react';
import { Line } from 'react-chartjs-2';
import { Link, useHistory } from 'react-router-dom';
import { useTheme } from 'styled-components';
import {
  FaCashRegister,
  FaShoppingBag,
  FaShoppingCart,
  FaUsers,
} from 'react-icons/fa';

import api from '../../services/api';

import ActivityIndicator from '../../components/ActivityIndicator';

import handleNumberFormat from '../../utils/handleNumberFormat';
import BRLMoneyFormat from '../../utils/BRLMoneyFormat';

import {
  Container,
  Wrapper,
  Cards,
  CardItem,
  LoadingContainer,
} from './styles';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import getDateKeyInTimeZone, {
  getValidTimeZone,
} from '../../utils/companyDate';

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const { rotinas, findRotinas, user } = useAuth();
  const companyTimeZone = getValidTimeZone(user?.empresa?.time_zone);
  const history = useHistory();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    todaysSales: 0,
    todaysOrders: 0,
    salesOfTheWeek: 0,
    yesterdaysSales: 0,
    yesterdaysOrders: 0,
    lastWeeksSales: 0,
    totalEarnings: 0,
    todaysActiveCustomers: 0,
    yesterdaysActiveCustomers: 0,
    salesOfTheMonth: {},
  });

  const hasPermission = useCallback(
    (): boolean => findRotinas('painel_controle')?.listar || false,
    [findRotinas],
  );

  useEffect(() => {
    if (rotinas && !hasPermission()) {
      addToast({
        type: 'error',
        title: 'Não autorizado.',
        description: 'Você não tem permissão.',
      });
      history.push('/monitor_pedidos');
    }

    async function loadData(): Promise<void> {
      setLoading(true);

      const currentDate = getDateKeyInTimeZone(new Date(), companyTimeZone);

      const response = await api.get('/dashboard', {
        params: {
          startDate: `${currentDate}T00:00:00`,
          endDate: `${currentDate}T23:59:59`,
        },
      });

      setData(response.data);
      setLoading(false);
    }

    loadData();
  }, [addToast, companyTimeZone, hasPermission, history, rotinas]);

  function calculatePercentage(
    startValue: number,
    endValue: number,
  ): React.ReactNode {
    if (startValue > endValue) {
      return (
        <span className="negative">
          -
          {handleNumberFormat(
            parseInt(String(((startValue - endValue) / startValue) * 100), 10),
          )}
          %
        </span>
      );
    }

    if (endValue > startValue) {
      return (
        <span className="positive">
          +
          {handleNumberFormat(
            parseInt(String(((endValue - startValue) / endValue) * 100), 10),
          )}
          %
        </span>
      );
    }

    return <span className="neutral">0%</span>;
  }

  function handleTotalBalanceFormat(): [string, string] {
    const total = Number(data.totalEarnings || 0)
      .toLocaleString('pt-BR', BRLMoneyFormat)
      .replace('R$', '')
      .split(',');

    return [total[0], total[1]];
  }

  return (
    <Container>
      <Wrapper>
        <header>
          <div>
            <h1>Painel de controle</h1>
            <h3>Aqui está um resumo de suas atividades</h3>
          </div>
          <span>
            Última atualização:{' '}
            <strong>
              {new Date().toLocaleDateString('pt-BR', {
                timeZone: companyTimeZone,
              })}
            </strong>
          </span>
        </header>
        {loading ? (
          <LoadingContainer>
            <ActivityIndicator />
          </LoadingContainer>
        ) : (
          <Cards>
            <CardItem>
              <header>
                <FaShoppingBag color={theme.colors.orange} />
                {calculatePercentage(data.yesterdaysSales, data.todaysSales)}
              </header>
              <main>
                <h3>
                  {Number(data.todaysSales).toLocaleString(
                    'pt-BR',
                    BRLMoneyFormat,
                  )}
                </h3>
              </main>
              <footer>
                <span>Vendas de hoje</span>
              </footer>
            </CardItem>
            <CardItem>
              <header>
                <FaShoppingCart color={theme.colors.pink} />
                {calculatePercentage(data.yesterdaysOrders, data.todaysOrders)}
              </header>
              <main>
                <h3>{handleNumberFormat(data.todaysOrders)}</h3>
              </main>
              <footer>
                <span>Qtde. de vendas</span>
              </footer>
            </CardItem>
            <CardItem>
              <header>
                <FaUsers color={theme.colors.green} />
                {calculatePercentage(
                  data.yesterdaysSales / data.yesterdaysOrders || 0,
                  data.todaysSales / data.todaysOrders || 0,
                )}
              </header>
              <main>
                <h3>
                  {Number(
                    data.todaysSales / data.todaysOrders || 0,
                  ).toLocaleString('pt-BR', BRLMoneyFormat)}
                </h3>
              </main>
              <footer>
                <span>Ticket médio</span>
              </footer>
            </CardItem>
            <CardItem>
              <header>
                <FaCashRegister color={theme.colors.red} />
                {calculatePercentage(
                  data.lastWeeksSales,
                  data.salesOfTheWeek,
                )}
              </header>
              <main>
                <h3>
                  {Number(data.salesOfTheWeek).toLocaleString(
                    'pt-BR',
                    BRLMoneyFormat,
                  )}
                </h3>
              </main>
              <footer>
                <span>Vendas da semana</span>
              </footer>
            </CardItem>
            <div className="chart-container">
              <h3>Gráfico de vendas</h3>
              <div style={{ position: 'relative', height: '100%' }}>
                <Line
                  id="chart"
                  type="line"
                  redraw={false}
                  options={{
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    scales: {
                      y: {
                        stacked: true,
                        beginAtZero: true,
                        ticks: {
                          callback: (value: number) => {
                            return Number(value).toLocaleString(
                              'pt-BR',
                              BRLMoneyFormat,
                            );
                          },
                        },
                      },
                    },
                  }}
                  data={{
                    responsive: true,
                    labels: Object.keys(data.salesOfTheMonth),
                    datasets: [
                      {
                        label: 'Total das vendas',
                        data: Object.values(data.salesOfTheMonth),
                        borderRadius: 5,
                        backgroundColor: theme.colors.main,
                        strokeWidth: 2,
                        borderSkipped: false,
                        borderColor: theme.colors.main,
                      },
                    ],
                  }}
                />
              </div>
            </div>
            <div className="total-balance">
              <header>
                <h2>Ganhos Totais</h2>
              </header>
              <main>
                <small>R$</small>
                <h2>{handleTotalBalanceFormat()[0]}</h2>
                <small>,{handleTotalBalanceFormat()[1]}</small>
              </main>
              <footer>
                <Link to="/dashboard/details">Ver detalhes</Link>
              </footer>
            </div>
          </Cards>
        )}
      </Wrapper>
    </Container>
  );
};

export default Dashboard;
